import cn from "classnames"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import useMedia from "../hooks/use_media"
import LoadingSpinner from "./loading_spinner"
import PreviewModal, {
  comparisonImage,
  PreviewComparisonSmall,
  type PreviewMinimaticSlide,
  type PreviewSlide,
} from "./preview_modal"
import SignSVGInline from "@/common/components/sign_svg_inline"
import tailwindTheme from "@/common/styles/theme.module.css"
import Canvas from "@/signomatic/components/canvas"
import { WarningTriangleColor } from "@/skylticons"

const srcSet = (src: string, src2x?: string | null) => {
  if (src2x) {
    return `${src} 1x, ${src2x} 2x`
  }

  return src
}

export const isMinimaticSlide = (slide: PreviewSlide): slide is PreviewMinimaticSlide => {
  return slide.type === "minimatic"
}

interface PreviewCarouselPreviewProps {
  slide: PreviewSlide
  slides: PreviewSlide[]
  alt?: string | null
  showPreview: () => void
}

const PreviewCarouselPreview: React.FC<PreviewCarouselPreviewProps> = ({ slide, slides, alt, showPreview }) => {
  const { t } = useTranslation()
  const minimaticSlide = slides.find(isMinimaticSlide)
  const minimaticActive = slide === minimaticSlide

  return (
    <div
      className="preview-img-wrapper relative flex h-[304px] w-full items-center justify-center overflow-hidden rounded-lg border border-white-20 bg-white-5 hover:cursor-pointer lg:h-[310px] lg:w-[384px] xl:h-[448px] xl:w-[672px] [@media(max-width:480px)]:aspect-[3/2] [@media(max-width:480px)]:h-full [@media(max-width:480px)]:max-h-[304px]"
      onClick={() => showPreview()}
    >
      {minimaticSlide && minimaticSlide.sign && (
        <div
          className={cn("signv2 pointer-events-none flex h-full w-full", {
            "absolute top-[-1000px]": !minimaticActive,
          })}
        >
          <div className="flex h-full w-full items-center justify-center overflow-hidden">
            <Canvas sign={minimaticSlide.sign} />
          </div>
        </div>
      )}
      {minimaticSlide && !minimaticSlide.sign && !minimaticSlide.loadingFailed && minimaticActive && (
        <div className="absolute bottom-0 right-0 flex items-center p-1">
          <span className="mr-2 text-base">{t("examples.updating_preview")}</span>
          <LoadingSpinner />
        </div>
      )}
      {minimaticSlide && !minimaticSlide.sign && minimaticSlide.loadingFailed && minimaticActive && (
        <div className="absolute bottom-0 right-0 flex items-center p-1">
          <span className="mr-2 text-base">{t("examples.preview_failed")}</span>
          <WarningTriangleColor className="warning h-8 w-8" />
        </div>
      )}
      {(slide.type === "svg" || (slide.type === "minimatic" && !slide.sign)) && (
        <div className="signv2 flex h-full w-full items-center justify-center overflow-hidden p-4 lg:p-8">
          <SignSVGInline
            component="div"
            svg={slide.svg}
            className="signv2 data-hj-suppress flex h-full w-full items-center justify-center [&_svg]:max-h-full [&_svg]:w-auto"
          />
        </div>
      )}
      {(slide.type === "image" || slide.type === "shopping") && slide.png && (
        <picture
          className={cn("extra-image flex h-full w-full items-center justify-center", {
            "shopping-image": slide.type === "shopping",
          })}
        >
          {slide.webp && <source srcSet={srcSet(slide.webp, slide.webp2x)} type="image/webp" />}
          <img srcSet={srcSet(slide.png, slide.png2x)} src={slide.png} alt={alt || ""} className="w-full" />
        </picture>
      )}
      {slide.type === "comparison" && (
        <>
          <div className="comparison h-full">
            <img
              className={cn("h-full max-w-none", { "ml-[160px] md:ml-[200px]": slide.comparison === "human" })}
              src={comparisonImage(slide)}
              alt={alt || ""}
            />
          </div>
          <div className="svg-preview-wrapper absolute inset-0 flex content-center items-center">
            <PreviewComparisonSmall svg={slide.svg} width={slide.width} height={slide.height} type={slide.comparison} />
          </div>
        </>
      )}
    </div>
  )
}

interface PreviewCarouselThumbnailsProps {
  slides: PreviewSlide[]
  thumbailsCount: number
  maxThumbnails: number
  currentSlide: number
  alt?: string | null
  showPreview: () => void
  setCurrentSlide: (slide: number) => void
}

const PreviewCarouselThumbnails: React.FC<PreviewCarouselThumbnailsProps> = ({
  slides,
  thumbailsCount,
  maxThumbnails,
  currentSlide,
  alt,
  showPreview,
  setCurrentSlide,
}) => {
  return (
    <div className="preview-thumbnails mt-3 flex">
      {slides.slice(0, maxThumbnails).map((thumbSlide, i) => (
        <button
          key={i}
          type="button"
          className={cn(
            "mr-3 flex h-12 w-12 items-center justify-center overflow-hidden rounded border-1 border-white-20 bg-white-10 lg:h-16 lg:w-16",
            {
              "active border-white shadow-[0_0_0_2px_rgba(0,153,243,1)]": i == currentSlide,
              "minimatic-thumb": thumbSlide.type === "minimatic",
            }
          )}
          onClick={e => {
            e.preventDefault(), setCurrentSlide(i)
          }}
        >
          {(thumbSlide.type === "svg" || thumbSlide.type === "minimatic") && (
            <SignSVGInline
              component="div"
              svg={thumbSlide.svg}
              className="signv2 data-hj-suppress flex h-full w-full items-center justify-center"
            />
          )}
          {thumbSlide.type === "shopping" && thumbSlide.png && (
            <img className="shopping-image max-h-full" src={thumbSlide.png} />
          )}
          {thumbSlide.type === "image" && thumbSlide.png && (
            <img className="extra-image max-h-full" src={thumbSlide.png} />
          )}
          {thumbSlide.type === "comparison" && (
            <img className="comparison max-h-full w-auto" src={comparisonImage(thumbSlide)} alt={alt || ""} />
          )}
        </button>
      ))}
      {thumbailsCount > maxThumbnails && (
        <button
          type="button"
          className="button button-medium button-secondary m-0 h-12 w-12 lg:h-16 lg:w-16"
          onClick={e => {
            e.preventDefault()
            showPreview()
          }}
        >
          +{thumbailsCount - maxThumbnails}
        </button>
      )}
    </div>
  )
}

interface PreviewCarouselProps {
  slides: PreviewSlide[]
  alt?: string | null
  className?: string
  currentSlide: number
  setCurrentSlide: (i: number) => void
}

const PreviewCarousel: React.FC<PreviewCarouselProps> = ({ slides, alt, className, currentSlide, setCurrentSlide }) => {
  const [maxThumbnails, setMaxThumbnails] = useState(6)
  const thumbailsCount = slides.length

  const slide = slides[currentSlide]

  const previewWrapperRef = useRef<HTMLDivElement>(null)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensMdmax})`, false)

  useEffect(() => {
    const thumbnailWidth = (desktop ? 64 : 48) + 12

    const handleResize = () => {
      const previewWidth = previewWrapperRef.current?.offsetWidth

      if (previewWidth) {
        let maxThumbnails = Math.floor(previewWidth / thumbnailWidth)

        if (slides.length > maxThumbnails) {
          maxThumbnails -= 1
        }

        setMaxThumbnails(maxThumbnails)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [slides.length, desktop])

  const [openPreview, setOpenPreview] = useState(false)

  const showPreview = () => setOpenPreview(true)
  const close = () => setOpenPreview(false)

  const minimaticSlide = slides.find(isMinimaticSlide)
  const minimaticSlideIndex = minimaticSlide && slides.indexOf(minimaticSlide)

  useEffect(() => {
    if (minimaticSlide?.sign && minimaticSlideIndex) {
      const handler = (obj: SignObjectState | null) => {
        if (obj) {
          setCurrentSlide(minimaticSlideIndex)
        }
      }

      minimaticSlide.sign.onActiveChanged.subscribe(handler)

      return () => minimaticSlide.sign?.onActiveChanged.unsubscribe(handler)
    }

    return
  }, [minimaticSlide?.sign, minimaticSlideIndex, setCurrentSlide])

  return (
    <div
      className={cn(
        "preview-carousel [&_.cutline]:hidden [&_.pdfoutline]:hidden [@media(max-width:480px)]:h-[calc((100vw-24px)*2/3+60px)]",
        className
      )}
    >
      <div className="px-3 lg:px-0 xl:w-auto">
        {slide && <PreviewCarouselPreview slide={slide} slides={slides} alt={alt} showPreview={showPreview} />}

        {openPreview && <PreviewModal slides={slides} initialSlide={currentSlide} close={close} />}

        <PreviewCarouselThumbnails
          slides={slides}
          thumbailsCount={thumbailsCount}
          maxThumbnails={maxThumbnails}
          currentSlide={currentSlide}
          alt={alt}
          showPreview={showPreview}
          setCurrentSlide={setCurrentSlide}
        />
      </div>
    </div>
  )
}

export default PreviewCarousel
